import React, { useCallback, useMemo, useState } from "react";
import SliderBase from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { Icon, Typography } from "@mui/material";

import RpMUIDialogBase from "@/components/modals/RpMUIModal";
import { CreativeMediaContent } from "@/features/campaigns/components/CreativeMediaPreview";
import { getCreativePackageLabel } from "@/features/campaigns/digital/launch-digital-campaign/helpers/creativeLabel";

type CreativeChannel = {
  creative_id?: number | string;
  fk_channel_id?: number | string;
  id?: number | string;
  channel_name?: string;
  attachment?: string;
  creative_name?: string;
  creative_type?: string;
  thumbnail_image?: string;
  thumbnail?: string;
  poster?: string;
};

type CreativePackage = {
  id?: number | string;
  is_selected?: boolean;
  package_name?: string;
  creative_name?: string;
  detail_1?: string;
  detail_2?: string;
  channels?: CreativeChannel[];
  [key: string]: unknown;
};

type CurrentCreativeReference = {
  creative_id?: number | string;
};

type CurrentCreativesProps = {
  creatives?: CurrentCreativeReference[];
  campaignData?: {
    campaign_creatives?: CreativePackage[];
  } | null;
  locationInfo?: {
    packages_data?: CreativePackage[];
  } | null;
};

type SlickArrowProps = {
  className?: string;
  style?: React.CSSProperties;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
};

const Slider = SliderBase as React.ComponentType<Record<string, any>>;
const RpMUIDialog = RpMUIDialogBase as React.ComponentType<Record<string, any>>;

function CurrentCreatives({
  creatives = [],
  campaignData,
  locationInfo,
}: CurrentCreativesProps) {
  const [open, setOpen] = useState<number | string | null>(null);

  const currentCreatives = useMemo(() => {
    if (
      locationInfo?.packages_data &&
      Array.isArray(locationInfo.packages_data)
    ) {
      return locationInfo.packages_data.filter(
        (pkg) => pkg.is_selected === true
      );
    }

    // Fallback to old logic if packages_data is not available
    if (!creatives || !Array.isArray(creatives) || creatives.length === 0) {
      return [];
    }
    if (
      !campaignData?.campaign_creatives ||
      !Array.isArray(campaignData.campaign_creatives)
    ) {
      return [];
    }
    return campaignData.campaign_creatives.filter((c) =>
      creatives.some((cc) => String(cc.creative_id) === String(c.id))
    );
  }, [
    locationInfo?.packages_data,
    creatives,
    campaignData?.campaign_creatives,
  ]);

  const getCreativeChannels = useMemo(() => {
    return currentCreatives.find((c) => c.id === open)?.channels || [];
  }, [currentCreatives, open]);

  const carouselSettings = useMemo(
    () => ({
      infinite: false,
      slidesToShow: 3,
      speed: 500,
      responsive: [
        {
          breakpoint: 1024,
          settings: {
            slidesToShow: 2,
            slidesToScroll: 1,
          },
        },
        {
          breakpoint: 991,
          settings: {
            slidesToShow: 1,
            slidesToScroll: 1,
          },
        },
        {
          breakpoint: 766,
          settings: {
            slidesToShow: 1,
            slidesToScroll: 1,
          },
        },
        {
          breakpoint: 480,
          settings: {
            slidesToShow: 1,
            slidesToScroll: 1,
          },
        },
      ],
      nextArrow: <SampleNextArrow />,
      prevArrow: <SamplePrevArrow />,
    }),
    []
  );

  const viewCreative = (c?: number | string) => () => {
    setOpen(c);
  };

  const close = useCallback(() => {
    setOpen(null);
  }, []);

  return (
    <>
      {currentCreatives.map((c, index) => {
        const creativePackageLabel = getCreativePackageLabel(c) || "N/A";

        return (
          <React.Fragment key={c.id}>
            <span
              style={{
                textDecoration: "underline",
                cursor: "pointer",
                color: "var(--primary-base)",
                whiteSpace: "normal",
                wordBreak: "break-word",
                overflowWrap: "anywhere",
                marginRight: "2px",
              }}
              onClick={viewCreative(c.id)}
            >
              {creativePackageLabel}
            </span>
            {index < currentCreatives.length - 1 && (
              <span style={{ marginRight: "2px" }}>,</span>
            )}
            <br />
          </React.Fragment>
        );
      })}

      {open && (
        <>
          <RpMUIDialog
            open={!!open}
            onClose={close}
            maxWidth="md"
            className="current-creatives-modal creative-carousel-arrows"
          >
            <div className="creative-carousel d-flex align-items-center justify-content-center ">
              <Slider {...carouselSettings} className="w-100">
                {getCreativeChannels.map((ch, index) => (
                  <div
                    key={
                      ch.creative_id ||
                      `${ch.fk_channel_id || ch.id || "channel"}-${index}`
                    }
                  >
                    <div className="creative-channel text-center p-3">
                      {ch.channel_name && (
                        <Typography
                          variant="caption"
                          component="p"
                          className="mb-2 text-center"
                        >
                          <span className="fw-sb">Channel:</span>{" "}
                          <Typography
                            color="text.primary"
                            variant="caption"
                            component="span"
                            className="fw-sb"
                          >
                            {ch.channel_name}
                          </Typography>
                        </Typography>
                      )}

                      <div className="d-flex justify-content-center mb-2">
                        <CreativeMediaContent
                          attachment={ch.attachment}
                          channelName={ch.channel_name}
                          creativeName={ch.creative_name}
                          creativeType={ch.creative_type}
                          thumbnail={
                            ch.thumbnail_image || ch.thumbnail || ch.poster
                          }
                          poster={ch.poster}
                          controls
                          imageClassName="img-fluid"
                        />
                      </div>

                      {ch.creative_name && (
                        <Typography
                          variant="caption"
                          component="p"
                          className="mt-2 text-center"
                        >
                          <span className="fw-sb">Title:</span>{" "}
                          <Typography
                            color="text.primary"
                            variant="caption"
                            component="span"
                            className="fw-sb"
                          >
                            {ch.creative_name}
                          </Typography>
                        </Typography>
                      )}
                    </div>
                  </div>
                ))}
              </Slider>
            </div>
          </RpMUIDialog>
        </>
      )}
    </>
  );
}

function SampleNextArrow(props: SlickArrowProps) {
  const { className, style, onClick } = props;
  return (
    <div
      className={`${className} d-flex align-items-center justify-content-center`}
      style={{
        ...style,
        width: "40px",
        height: "40px",
        borderRadius: "var(--radius-control)",
        zIndex: 2,
      }}
      onClick={onClick}
    >
      <Icon color={onClick ? "primary" : "disabled"} sx={{ fontSize: "24px" }}>
        navigate_next
      </Icon>
    </div>
  );
}

function SamplePrevArrow(props: SlickArrowProps) {
  const { className, style, onClick } = props;
  return (
    <div
      className={`${className} d-flex align-items-center justify-content-center`}
      style={{
        ...style,
        width: "40px",
        height: "40px",
        borderRadius: "var(--radius-control)",
        zIndex: 2,
      }}
      onClick={onClick}
    >
      <Icon color={onClick ? "primary" : "disabled"} sx={{ fontSize: "24px" }}>
        navigate_before
      </Icon>
    </div>
  );
}

export default CurrentCreatives;
